import React, { Fragment } from 'react'
import PropTypes from 'prop-types'
import LedgerRow from './LedgerRow'
import Media from 'react-media'
import styled from '@emotion/styled'
import { css } from '@emotion/core'

import { StyledP } from 'portal-modules/payments/components/atoms'
import { getEmbeddedViewFlag } from 'portal-modules/common/utils/get-embedded-flag'

const StyledTable = styled('table')`
  width: 100%;
  max-width: 100%;
  border-collapse: collapse;
  margin-top: 16px;
  text-align: left;

  @media only screen and (min-width: 768px) and (max-width: 959px) {
    margin-top: 0;
  }

  @media only screen and (max-width: 768px) {
    th, td{
      padding: ${({ fromInvoice }) => fromInvoice && '16px 0'}
    }

    td:first-child{
      padding-left: ${({ fromInvoice }) => fromInvoice && '10px'};
    }

    td:last-child{
      padding-right: ${({ fromInvoice }) => fromInvoice && '0'};
    }
  }
`

const sharedColStyle = css`
  padding-left: 10px;
  padding-right: 10px;

  :first-child {
    padding-left: 24px;
  }

  :last-child {
    padding-right: 24px;
  }

  @media only screen and (min-width: 768px) {
    :first-child {
      padding-left: 32px;
    }

    :last-child {
      padding-right: 32px;
    }
  }

  @media only screen and (min-width: 960px) {
    :first-child {
      padding-left: 16px;
    }

    :last-child {
      padding-right: 16px;
    }
  }

  &.align--left {
    text-align: left;
  }

  &.align--right {
    text-align: right;
  }
`

const StyledTd = styled('td')`
  font-size: 13px;
  padding-top: 16px;
  padding-bottom: 16px;
  color: ${({ theme }) => getEmbeddedViewFlag() && theme.secondary.base};
  ${sharedColStyle}
`

const StyledTitleTh = styled('th')`
  background-color: ${({ theme }) => !getEmbeddedViewFlag() && !theme.mode.isDark ? theme.gray.highlight : theme.mode.isDark && 'transparent'};
  font-size: 12px;
  font-style: italic;
  padding: 4px 24px;

  @media only screen and (min-width: 768px) {
    padding-left: 32px;
  }

  @media only screen and (min-width: 960px) {
    padding-left: 16px;
  }
`

const StyledTr = styled('tr')`
  border-bottom: 1px solid ${({ theme }) => theme.gray.light};
  vertical-align: top;
`

const StyledTh = styled('th')`
  font-size: 11px;
  font-style: italic;
  font-weight: 300;
  padding-top: 8px;
  padding-bottom: 8px;
  color: ${({ theme }) => getEmbeddedViewFlag() && theme.secondary.base};
  ${sharedColStyle}
`

const LedgerTable = props => {
  const getColProps = (titleId = '') => (id = '') => {
    const { align = 'left', name } =
      props.columns.find(col => col?.id === id) || {}
    return {
      className: `align--${align || 'left'}`,
      headers: `${titleId} ${name}`.trim(),
      key: id,
    }
  }

  const {
    columns = [],
    rows = [],
    groups = [],
    renderRow,
    className,
    customerId,
    fromInvoice = false,
  } = props
  return (
    <Media query='(max-width: 768px)'>
      {isSmallScreen => (
        <StyledTable className={className} fromInvoice={fromInvoice}>
          <thead>
            <StyledTr>
              {columns.map(col => (
                  <StyledTh
                    key={col?.id}
                    className={`align--${col?.align || 'left'}`}
                    id={col?.id}
                    scope='col'
                    style={!isSmallScreen ? col?.style : col?.style2}
                  >
                    {col?.name}
                  </StyledTh>
                )
              )}
            </StyledTr>
          </thead>
          <tbody>
            {rows.length > 0 &&
              rows.map((row, i) =>
                renderRow({
                  ...row,
                  key: `row-${i}`,
                  getColProps: getColProps(),
                  customerId,
                }),
              )}
            {groups.length > 0 &&
              groups.map(({ title, rows = [], note }, i) => {
                const titleId = `group-${i}`
                return (
                  <Fragment key={titleId}>
                    {title && (
                      <LedgerRow>
                        <StyledTitleTh
                          id={titleId}
                          colSpan={columns.length}
                          scope='row'
                        >
                          {title}
                        </StyledTitleTh>
                      </LedgerRow>
                    )}
                    {rows.map((row, i) =>
                      renderRow({
                        ...row,
                        key: `row-${i}`,
                        getColProps: getColProps(titleId),
                      }),
                    )}
                    {note && (
                      <LedgerRow>
                        <StyledTd colSpan={columns.length}>
                          <StyledP>{note}</StyledP>
                        </StyledTd>
                      </LedgerRow>
                    )}
                  </Fragment>
                )
              })}
          </tbody>
        </StyledTable>
      )}
    </Media>
  )
}

LedgerTable.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.object).isRequired,
  rows: PropTypes.arrayOf(PropTypes.object),
  groups: PropTypes.arrayOf(PropTypes.object),
  renderRow: PropTypes.func.isRequired,
  className: PropTypes.string,
  customerId: PropTypes.number,
}

LedgerTable.Row = LedgerRow
LedgerTable.Col = StyledTd

export default LedgerTable
